import React from 'react';
import { Card, CardContent, Typography, Box, Grid, Chip } from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
interface OrderData {
  id: string | number;
  ref: string;
  status: string;
  supplier: string;
  warehouse: string;
  from_request: string;
  created_at: string;
}

interface OrderDetailsCardProps {
  ddt?: {
    order?: OrderData;
  };
}

const OrderDetailsCard = ({ ddt }: OrderDetailsCardProps) => {
  const { t } = useTranslation();

  if (!ddt?.order) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, color: 'text.secondary' }}>
        <InfoIcon fontSize="small" />
        <Typography variant="body2">
          {t('order.notFound', 'Nessun dettaglio ordine disponibile')}
        </Typography>
      </Box>
    );
  }

  const { ref, status, supplier, warehouse, from_request, created_at } = ddt.order;

  const getStatusColor = (currentStatus: string) => {
    const formatted = currentStatus?.toLowerCase() || '';
    if (formatted.includes('complete') || formatted.includes('delivered')) return 'success';
    if (formatted.includes('pending') || formatted.includes('process')) return 'warning';
    if (formatted.includes('cancel')) return 'error';
    return 'primary';
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        mb: 3
      }}>
      {/* Structural Card Header Bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShoppingBagIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {ref}
          </Typography>
        </Box>

        <Chip
          label={status}
          size="small"
          color={getStatusColor(status)}
          sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem' }}
        />
      </Box>

      {/* Main Info Payload Matrix */}
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Grid container spacing={3}>
          {/* Supplier Info Node */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('global.supplier')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {supplier}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Warehouse Info Node */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarehouseIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('global.warehouse')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {warehouse}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Reference Request Node */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ReceiptLongIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('entities.order.from_request')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {from_request}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Creation Timeline Node */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {t('entities.order.created_at')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {dayjs(created_at).format('YYYY-MM-DD HH:mm')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OrderDetailsCard;
